/**
 * `ModuleEtv` — the Stammdaten → Kommunikation → Versammlungen (Online-ETV)
 * screen of the HERO product mock (1:1 with the prototype `showEtv` block). Shows
 * a live "Online-Eigentümerversammlung 2026": header with quorum stats, the
 * agenda rail (TOP 1–8, TOP 4 running), and the TOP-4 result panel with the
 * announced outcome + live vote bars. The module sidebar is the shared
 * `StammdatenSidebar`.
 */
import {
  CheckCircle2,
  ChevronRight,
  FileText,
  Radio,
  Square,
  Video,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";

import { StammdatenSidebar } from "../../hero-stammdaten-sidebar";
import { H, MODULE_HEIGHT } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";

/** Mono eyebrow used for the "Tagesordnung" / "Live-Ergebnis" labels. */
const MONO_LABEL: CSSProperties = {
  fontFamily: H.label,
  fontSize: 10,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "rgba(11,15,25,0.34)",
};

/** A completed agenda row (TOP 1–3): muted check + ellipsised label. */
function AgendaDone(props: { readonly label: string }): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "6px 9px",
        borderRadius: 8,
        fontSize: 12,
        color: "rgba(11,15,25,0.5)",
      }}
    >
      <span style={{ display: "inline-flex", color: H.accent, flexShrink: 0 }}>
        <CheckCircle2 width={15} height={15} strokeWidth={2} />
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {props.label}
      </span>
    </div>
  );
}

/** A pending agenda row (TOP 5–8): hollow ring + ellipsised label. */
function AgendaPending(props: { readonly label: string }): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "6px 9px",
        borderRadius: 8,
        fontSize: 12,
        color: "rgba(11,15,25,0.62)",
      }}
    >
      <span
        style={{
          width: 15,
          height: 15,
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          style={{
            width: 11,
            height: 11,
            borderRadius: "50%",
            border: "1.6px solid #CDD2D9",
          }}
        />
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {props.label}
      </span>
    </div>
  );
}

/** A live-result vote bar (Ja / Nein / Enthaltung). */
function VoteBar(props: {
  readonly label: string;
  readonly value: string;
  readonly percent: number;
  readonly color: string;
}): ReactNode {
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: 12,
          marginBottom: 4,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontWeight: 600,
            color: "#0B0F19",
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: props.color,
            }}
          />
          {props.label}
        </span>
        <span
          style={{
            color: "rgba(11,15,25,0.6)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {props.value}
        </span>
      </div>
      <div
        style={{
          height: 7,
          borderRadius: 4,
          background: "#EEF0F3",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${props.percent}%`,
            height: "100%",
            borderRadius: 4,
            background: props.color,
          }}
        />
      </div>
    </div>
  );
}

export function ModuleEtv(props: ModuleProps): ReactNode {
  const t = useTranslations("marketing.moduleEtv");

  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT }}>
      <StammdatenSidebar active="etv" setActive={props.setActive} />

      <div
        style={{ flex: 1, minWidth: 0, padding: "20px 22px", overflow: "auto" }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 14,
              paddingBottom: 12,
              borderBottom: "1px solid #EEF0F3",
              marginBottom: 13,
              flexShrink: 0,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    background: "#FCEBEC",
                    color: "#D8453E",
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    padding: "3px 8px",
                    borderRadius: 6,
                  }}
                >
                  <span
                    className="mkhero-kp-live"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#D8453E",
                    }}
                  />
                  {t("live")}
                </span>
                <span
                  style={{ fontSize: 16, fontWeight: 700, color: "#0B0F19" }}
                >
                  {t("onlineEigentuemerversammlung2026")}
                </span>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(11,15,25,0.5)",
                  marginTop: 5,
                }}
              >
                {t("wegAkazienhofAkazienweg5")}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  fontSize: 12,
                  color: "rgba(11,15,25,0.7)",
                  marginTop: 8,
                  flexWrap: "wrap",
                }}
              >
                <span>
                  {t.rich("anwesendZaehler", {
                    b: (chunks: ReactNode): ReactNode => (
                      <b style={{ fontWeight: 700, color: "#0B0F19" }}>
                        {chunks}
                      </b>
                    ),
                  })}
                </span>
                <span style={{ opacity: 0.3 }}>·</span>
                <span>{t("k8471000Mea847")}</span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    background: H.accentSoft,
                    color: H.accent,
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "2px 9px",
                    borderRadius: 6,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: H.accent,
                    }}
                  />
                  {t("beschlussfaehig")}
                </span>
              </div>
            </div>
            <button
              type="button"
              style={{
                appearance: "none",
                cursor: "pointer",
                fontFamily: H.sans,
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                height: 36,
                padding: "0 15px",
                border: "none",
                borderRadius: 9,
                background: "#0B0F19",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              <Video width={16} height={16} strokeWidth={2} />
              {t("videokonferenzOeffnen")}
            </button>
          </div>

          {/* Body: agenda rail + result panel */}
          <div style={{ display: "flex", gap: 18, flex: 1, minHeight: 0 }}>
            {/* Agenda rail */}
            <div
              style={{
                width: 244,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <div style={{ ...MONO_LABEL, marginBottom: 9 }}>
                {t("tagesordnung")}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  overflow: "hidden",
                }}
              >
                <AgendaDone label={t("top1BegruessungBeschlussfaehigkeit")} />
                <AgendaDone label={t("top2Jahresabrechnung2025")} />
                <AgendaDone label={t("top3EntlastungVerwaltung")} />
                {/* TOP 4 — running */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    padding: "8px 9px",
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#0B0F19",
                    background: H.accentSoft,
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      color: H.accent,
                      flexShrink: 0,
                    }}
                  >
                    <Radio width={15} height={15} strokeWidth={2} />
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("top4Wirtschaftsplan2026")}
                  </span>
                  <span
                    style={{
                      fontSize: 8.5,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      color: H.accent,
                      flexShrink: 0,
                    }}
                  >
                    {t("laeuft")}
                  </span>
                </div>
                <AgendaPending
                  label={t("top5InstandhaltungsruecklageErhoehen")}
                />
                <AgendaPending label={t("top6FassadensanierungDaemmung")} />
                <AgendaPending label={t("top7HausmeisterdienstNeuVergeben")} />
                <AgendaPending label={t("top8Verschiedenes")} />
              </div>
            </div>

            {/* Result panel */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0B0F19" }}>
                {t("top4Wirtschaftsplan2026")}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(11,15,25,0.5)",
                  marginTop: 3,
                }}
              >
                {t("beschlussEinfacheMehrheitNach")}
              </div>

              {/* Announced outcome */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginTop: 13,
                  padding: "11px 15px",
                  borderRadius: 11,
                  background: H.accentSoft,
                  border: `1px solid ${H.accentBorder}`,
                }}
              >
                <span
                  style={{
                    fontFamily: H.label,
                    fontSize: 9.5,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: H.accent,
                  }}
                >
                  {t("verkuendet")}
                </span>
                <span
                  style={{ fontSize: 17, fontWeight: 700, color: H.accent }}
                >
                  {t("angenommen")}
                </span>
                <span
                  style={{
                    marginLeft: "auto",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 11,
                    fontWeight: 600,
                    color: H.accent,
                  }}
                >
                  <CheckCircle2 width={14} height={14} strokeWidth={2} />
                  {t("einfacheMehrheitErreicht")}
                </span>
              </div>

              {/* Live result bars */}
              <div style={{ marginTop: 14 }}>
                <div
                  style={{
                    ...MONO_LABEL,
                    color: "rgba(11,15,25,0.34)",
                    marginBottom: 10,
                  }}
                >
                  {t("liveErgebnis")}
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 9 }}
                >
                  <VoteBar
                    label={t("ja")}
                    value="712 MEA · 84,1 %"
                    percent={84.1}
                    color={H.accent}
                  />
                  <VoteBar
                    label={t("nein")}
                    value="95 MEA · 11,2 %"
                    percent={11.2}
                    color="#D8453E"
                  />
                  <VoteBar
                    label={t("enthaltung")}
                    value="40 MEA · 4,7 %"
                    percent={4.7}
                    color="#B6BCC6"
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 13,
                    marginTop: 11,
                    fontSize: 11.5,
                    color: "rgba(11,15,25,0.5)",
                  }}
                >
                  <span>
                    {t.rich("abgegebeneStimmen", {
                      b: (chunks: ReactNode): ReactNode => (
                        <b style={{ color: "#0B0F19", fontWeight: 600 }}>
                          {chunks}
                        </b>
                      ),
                    })}
                  </span>
                  <span style={{ opacity: 0.3 }}>·</span>
                  <span>{t("schwelle50AbgegebenenStimmen")}</span>
                </div>
              </div>

              <div style={{ flex: 1 }} />

              <button
                type="button"
                style={{
                  appearance: "none",
                  cursor: "pointer",
                  fontFamily: H.sans,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                  height: 38,
                  padding: "0 16px",
                  border: "none",
                  borderRadius: 9,
                  background: H.accent,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  alignSelf: "flex-start",
                  marginTop: 10,
                }}
              >
                {t("topAbschliessenWeiter")}
                <ChevronRight width={15} height={15} strokeWidth={2.2} />
              </button>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingTop: 11,
              borderTop: "1px solid #EEF0F3",
              marginTop: 13,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11.5,
                color: "rgba(11,15,25,0.42)",
              }}
            >
              <FileText width={13} height={13} strokeWidth={1.8} />
              {t("protokollWirdAutomatischErstellt")}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                fontWeight: 500,
                color: "rgba(11,15,25,0.55)",
                cursor: "pointer",
                padding: "5px 12px",
                border: "1px solid #E2E5EA",
                borderRadius: 8,
              }}
            >
              <Square width={13} height={13} strokeWidth={1.9} />
              {t("versammlungBeenden")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
